import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '@services/api';
import { useTranslationStore } from '@stores/translationStore';
import { useSessionStore } from '@stores/sessionStore';
import { emitActivityChanged } from '@utils/activityEvents';
import { promptVerifyToOrder } from '@utils/verifyToOrder';
import StatusCard, { StatusCardMetadata } from './StatusCard';
import './MenuCard.scss';

// ── Types matching API DTOs ──

interface MenuOption {
  id: number;
  nameKey: string;
  additionalPrice: number;
  isDefault: boolean;
}

interface MenuOptionGroup {
  id: number;
  nameKey: string;
  selectionType?: string;
  minSelections: number;
  maxSelections: number;
  options: MenuOption[];
}

interface MenuItem {
  id: number;
  nameKey: string;
  descriptionKey?: string;
  imagePath?: string;
  price?: number;
  isIncludedInMeal: boolean;
  maxQuantity?: number;
  stockQuantity?: number;
  optionGroups?: MenuOptionGroup[];
}

interface MenuCategory {
  id: number;
  nameKey: string;
  descriptionKey?: string;
  imagePath?: string;
  maxItemsPerGuestPerDay?: number;
  items: MenuItem[];
}

interface StationMenuData {
  stationId: number;
  stationName: string;
  stationNameKey?: string;
  trackingMode: string;
  fulfillmentMode: string;
  welcomeMessage?: string;
  isServiceActive: boolean;
  acceptsOrdersWhenClosed: boolean;
  maxOrdersPerDay: number;
  maxOrdersPerSession: number;
  ordersPlacedToday: number;
  canOrderMore: boolean;
  isBeyondFreeLimit: boolean;
  freeOrdersRemaining: number;
  folioOrdersEnabled: boolean;
  guestName: string;
  roomNumber: string;
  guestCount: number;
  usedGuestNames: string[];
  categories: MenuCategory[];
  translations?: Record<string, string>;
  currencySymbol?: string;
}

interface CartItem {
  menuItem: MenuItem;
  quantity: number;
  selectedOptionIds: number[];
  notes: string;
}

export interface MenuCardMetadata {
  type: 'menu';
  stationId: number;
  stationName: string;
  hotelSlug: string;
}

interface MenuCardProps {
  metadata: MenuCardMetadata;
  messageId?: string;
  onOrderPlaced?: (orderId: string, statusMeta?: StatusCardMetadata) => void;
}

export default function MenuCard({ metadata, messageId, onOrderPlaced }: MenuCardProps) {
  const { t, language } = useTranslationStore();
  const { personalName, isDeviceTrusted } = useSessionStore();
  const [menuData, setMenuData] = useState<StationMenuData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const [itemNotes, setItemNotes] = useState<Record<number, string>>({});
  const [itemOptions, setItemOptions] = useState<Record<number, number[]>>({});
  const [placing, setPlacing] = useState(false);
  const [orderResult, setOrderResult] = useState<{ orderId: string; orderNumber?: string; message: string; items?: { name: string; quantity: number; price?: number }[]; totalAmount?: number } | null>(null);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [orderName, setOrderName] = useState('');
  const [showNamePicker, setShowNamePicker] = useState(false);
  const [sharedSlotConfirm, setSharedSlotConfirm] = useState<{ message: string; remainingSlots: number } | null>(null);
  // Order confirmation step — shown for EVERY order (free or charged) before it's placed,
  // so the guest always confirms the overall order (parity with the WhatsApp Flow path).
  const [confirming, setConfirming] = useState(false);

  const { stationId, hotelSlug } = metadata;
  const sym = menuData?.currencySymbol || '₪';

  // Fetch menu from the station endpoint   
  const loadMenu = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get<StationMenuData>(`/api/v1/${hotelSlug}/stations/${stationId}/menu?lang=${language}`);
      setMenuData(data);
      if (data.categories?.length > 0) {
        setActiveCategory(data.categories[0].id);
      }
      // Pre-select default options for all items
      const defaults: Record<number, number[]> = {};
      for (const cat of data.categories || []) {
        for (const item of cat.items || []) {
          const defs: number[] = [];
          for (const og of item.optionGroups || []) {
            for (const opt of og.options || []) {
              if (opt.isDefault) defs.push(opt.id);
            }
          }
          if (defs.length > 0) defaults[item.id] = defs;
        }
      }
      setItemOptions(defaults);
      // Pre-fill order name: use device personal name for multi-guest, session name for single-guest
      if (data.guestCount > 1 && personalName) {
        setOrderName(personalName);
      } else if (data.guestCount <= 1 && data.guestName) {
        setOrderName(data.guestName);
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e.response?.data?.error || 'Failed to load menu');
    } finally {
      setLoading(false);
    }
  }, [hotelSlug, stationId, language]);

  useEffect(() => {
    loadMenu();
  }, [loadMenu]);

  // Resolve translation keys to display text
  // Uses server-side translations from menu data first, then falls back to translation store
  const resolveText = (key?: string, fallback?: string) => {
    if (!key) return fallback || '';
    // Primary: use translations dict from server response
    if (menuData?.translations?.[key]) return menuData.translations[key];
    // Fallback: translation store
    const translated = t(key);
    return translated !== key ? translated : (fallback || key);
  };

  // Cart helpers
  const getCartItem = (menuItemId: number) => cart.find(c => c.menuItem.id === menuItemId);
  const cartCount = useMemo(() => cart.reduce((sum, c) => sum + c.quantity, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((sum, c) => {
    let itemTotal = (c.menuItem.price ?? 0) * c.quantity;
    // Add option prices
    for (const optId of c.selectedOptionIds) {
      for (const cat of menuData?.categories || []) {
        for (const item of cat.items) {
          for (const og of item.optionGroups || []) {
            const opt = og.options.find(o => o.id === optId);
            if (opt) itemTotal += (opt.additionalPrice ?? 0) * c.quantity;
          }
        }
      }
    }
    return sum + itemTotal;
  }, 0), [cart, menuData]);

  const addToCart = (item: MenuItem) => {
    const existing = getCartItem(item.id);
    if (existing) {
      if (item.maxQuantity && existing.quantity >= item.maxQuantity) return;
      setCart(prev => prev.map(c =>
        c.menuItem.id === item.id ? { ...c, quantity: c.quantity + 1 } : c
      ));
    } else {
      setCart(prev => [...prev, {
        menuItem: item,
        quantity: 1,
        selectedOptionIds: itemOptions[item.id] || [],
        notes: itemNotes[item.id] || '',
      }]);
    }
  };

  const removeFromCart = (itemId: number) => {
    const existing = getCartItem(itemId);
    if (!existing) return;
    if (existing.quantity <= 1) {
      setCart(prev => prev.filter(c => c.menuItem.id !== itemId));
    } else {
      setCart(prev => prev.map(c =>
        c.menuItem.id === itemId ? { ...c, quantity: c.quantity - 1 } : c
      ));
    }
  };

  const updateCartItemNotes = (itemId: number, notes: string) => {
    setItemNotes(prev => ({ ...prev, [itemId]: notes }));
    setCart(prev => prev.map(c =>
      c.menuItem.id === itemId ? { ...c, notes } : c
    ));
  };

  const toggleOption = (itemId: number, optionId: number, group: MenuOptionGroup) => {
    setItemOptions(prev => {
      const current = prev[itemId] || [];
      const groupOptionIds = group.options.map(o => o.id);

      if (group.selectionType === 'single' || group.maxSelections === 1) {
        // Replace: remove all from this group, add this one
        const filtered = current.filter(id => !groupOptionIds.includes(id));
        const newOpts = current.includes(optionId) ? filtered : [...filtered, optionId];
        return { ...prev, [itemId]: newOpts };
      } else {
        // Multi-select toggle
        if (current.includes(optionId)) {
          return { ...prev, [itemId]: current.filter(id => id !== optionId) };
        }
        // Check max
        const groupCount = current.filter(id => groupOptionIds.includes(id)).length;
        if (group.maxSelections && groupCount >= group.maxSelections) return prev;
        return { ...prev, [itemId]: [...current, optionId] };
      }
    });
    // Update cart item if in cart
    setCart(prev => prev.map(c => {
      if (c.menuItem.id !== itemId) return c;
      return { ...c, selectedOptionIds: itemOptions[itemId] || c.selectedOptionIds };
    }));
  };

  // This order will post a folio charge (beyond free allowance, folio enabled). Free orders aren't gated.
  const orderWillCharge = !!(menuData?.isBeyondFreeLimit && menuData?.folioOrdersEnabled);

  // Place order
  const placeOrder = async (confirmSharedSlot = false) => {
    if (cart.length === 0 || placing) return;

    // Chargeable order on a browse-only device: clear the room-phone gate first, then retry. Free orders pass.
    if (orderWillCharge && !isDeviceTrusted()) {
      promptVerifyToOrder(() => placeOrder(confirmSharedSlot));
      return;
    }

    // Check if multi-guest and name needed
    if (menuData && menuData.guestCount > 1 && !orderName.trim()) {
      setShowNamePicker(true);
      return;
    }

    setPlacing(true);
    setSharedSlotConfirm(null);
    try {
      const { data } = await apiClient.post<{ orderId: string; orderNumber?: string; message: string }>(`/api/v1/${hotelSlug}/stations/${stationId}/order`, {
        items: cart.map(c => ({
          menuItemId: c.menuItem.id,
          quantity: c.quantity,
          notes: c.notes || undefined,
          optionIds: c.selectedOptionIds.length > 0 ? c.selectedOptionIds : undefined,
        })),
        orderedByName: orderName || undefined,
        messageId: messageId || undefined,
        confirmUseSharedSlot: confirmSharedSlot || undefined,
      });

      // Build order items for the tracker
      const withinFreeLimit = menuData && !menuData.isBeyondFreeLimit;
      const orderItems = cart.map(c => ({
        name: resolveText(c.menuItem.nameKey, c.menuItem.nameKey),
        quantity: c.quantity,
        price: withinFreeLimit ? undefined : (c.menuItem.price ?? undefined),
      }));
      const total = cart.reduce((sum, c) => {
        let itemTotal = (c.menuItem.price ?? 0) * c.quantity;
        for (const optId of c.selectedOptionIds) {
          for (const cat of menuData?.categories || []) {
            for (const item of cat.items) {
              for (const og of item.optionGroups || []) {
                const opt = og.options.find(o => o.id === optId);
                if (opt) itemTotal += opt.additionalPrice * c.quantity;
              }
            }
          }
        }
        return sum + itemTotal;
      }, 0);
      setOrderResult({
        orderId: data.orderId,
        orderNumber: data.orderNumber || undefined,
        message: data.message || 'Order placed!',
        items: orderItems,
        totalAmount: withinFreeLimit ? undefined : (total > 0 ? total : undefined),
      });
      setCart([]);

      // Build status card metadata and persist to message
      const statusMeta: StatusCardMetadata = {
        type: 'order',
        referenceId: data.orderId,
        orderNumber: data.orderNumber || undefined,
        status: 'Pending',
        stationName: metadata.stationName,
        items: orderItems,
        totalAmount: withinFreeLimit ? undefined : (total > 0 ? total : undefined),
      };

      onOrderPlaced?.(data.orderId, statusMeta);
      emitActivityChanged();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { requiresConfirmation?: boolean; error?: string; remainingSessionSlots?: number } } };
      // Handle shared-slot confirmation (409 with RequiresConfirmation)
      if (e.response?.status === 409 && e.response?.data?.requiresConfirmation) {
        setSharedSlotConfirm({
          message: e.response.data.error || 'Personal daily limit reached. This order will use another guest\'s slot.',
          remainingSlots: e.response.data.remainingSessionSlots || 0,
        });
      } else {
        setError(e.response?.data?.error || 'Failed to place order');
      }
    } finally {
      setPlacing(false);
    }
  };

  // ── Renders ──

  if (loading) {
    return (
      <div className="menu-card menu-card--loading">
        <div className="menu-card__header">
          <span className="menu-card__header-icon">🍽️</span>
          <span className="menu-card__header-title">{metadata.stationName}</span>
        </div>
        <div className="menu-card__loader">
          <div className="menu-card__spinner" />
          <span>Loading menu...</span>
        </div>
      </div>
    );
  }

  if (error || !menuData) {
    return (
      <div className="menu-card menu-card--error">
        <div className="menu-card__header">
          <span className="menu-card__header-icon">🍽️</span>
          <span className="menu-card__header-title">{metadata.stationName}</span>
        </div>
        <div className="menu-card__error">{error || 'Menu unavailable'}</div>
      </div>
    );
  }

  if (orderResult) {
    const trackerMetadata: StatusCardMetadata = {
      type: 'order',
      referenceId: orderResult.orderId,
      orderNumber: orderResult.orderNumber,
      status: 'Pending',
      stationName: metadata.stationName,
      items: orderResult.items,
      totalAmount: orderResult.totalAmount,
    };
    return <StatusCard metadata={trackerMetadata} />;
  }

  if (!menuData.canOrderMore) {
    return (
      <div className="menu-card menu-card--limit">
        <div className="menu-card__header">
          <span className="menu-card__header-icon">🍽️</span>
          <span className="menu-card__header-title">{menuData.stationName}</span>
        </div>
        <div className="menu-card__limit">Daily order limit reached ({menuData.ordersPlacedToday}/{menuData.maxOrdersPerSession})</div>
      </div>
    );
  }

  const currentCategory = menuData.categories.find(c => c.id === activeCategory) || menuData.categories[0];

  return (
    <div className="menu-card">
      {/* Header */}
      <div className="menu-card__header">
        <span className="menu-card__header-icon">🍽️</span>
        <span className="menu-card__header-title">{menuData.stationName}</span>
        {!menuData.isServiceActive && menuData.acceptsOrdersWhenClosed && (
          <span className="menu-card__header-badge">Pre-order</span>
        )}
      </div>

      {/* Category tabs */}
      {menuData.categories.length > 1 && (
        <div className="menu-card__categories">
          {menuData.categories.map(cat => (
            <button
              key={cat.id}
              className={`menu-card__cat-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {resolveText(cat.nameKey)}
            </button>
          ))}
        </div>
      )}

      {/* Items */}
      <div className="menu-card__items">
        {currentCategory?.items.map(item => {
          const inCart = getCartItem(item.id);
          const isExpanded = expandedItem === item.id;
          const isUnavailable = item.stockQuantity != null && item.stockQuantity <= 0;

          return (
            <div
              key={item.id}
              className={`menu-card__item ${inCart ? 'in-cart' : ''} ${isUnavailable ? 'unavailable' : ''}`}
            >
              <div className="menu-card__item-main" onClick={() => !isUnavailable && setExpandedItem(isExpanded ? null : item.id)}>
                <div className="menu-card__item-info">
                  <span className="menu-card__item-name">{resolveText(item.nameKey)}</span>
                  {item.descriptionKey && (
                    <span className="menu-card__item-desc">{resolveText(item.descriptionKey)}</span>
                  )}
                  <div className="menu-card__item-meta">
                    {item.price != null && item.price > 0 && (menuData.isBeyondFreeLimit || !item.isIncludedInMeal) && (
                      <span className="menu-card__item-price">{sym}{item.price.toFixed(2)}</span>
                    )}
                    {item.isIncludedInMeal && !menuData.isBeyondFreeLimit && (
                      <span className="menu-card__item-included">Included</span>
                    )}
                  </div>
                </div>

                {/* Quantity controls */}
                {!isUnavailable && (
                  <div className="menu-card__qty">
                    {inCart ? (
                      <>
                        <button className="menu-card__qty-btn" onClick={(e) => { e.stopPropagation(); removeFromCart(item.id); }}>−</button>
                        <span className="menu-card__qty-val">{inCart.quantity}</span>
                        <button
                          className="menu-card__qty-btn"
                          onClick={(e) => { e.stopPropagation(); addToCart(item); }}
                          disabled={item.maxQuantity != null && inCart.quantity >= item.maxQuantity}
                        >+</button>
                      </>
                    ) : (
                      <button className="menu-card__add-btn" onClick={(e) => { e.stopPropagation(); addToCart(item); }}>+</button>
                    )}
                  </div>
                )}
                {isUnavailable && (
                  <span className="menu-card__item-sold-out">Sold out</span>
                )}
              </div>

              {/* Expanded: options + notes */}
              {isExpanded && !isUnavailable && (
                <div className="menu-card__item-expanded">
                  {/* Option groups */}
                  {item.optionGroups && item.optionGroups.length > 0 && item.optionGroups.map(group => (
                    <div key={group.id} className="menu-card__option-group">
                      <span className="menu-card__option-label">
                        {resolveText(group.nameKey)}
                        {group.minSelections > 0 && <span className="menu-card__option-req"> (required)</span>}
                      </span>
                      <div className="menu-card__options">
                        {group.options.map(opt => {
                          const isSelected = (itemOptions[item.id] || []).includes(opt.id);
                          return (
                            <button
                              key={opt.id}
                              className={`menu-card__option ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleOption(item.id, opt.id, group)}
                            >
                              <span className="menu-card__option-check">{isSelected ? '✓' : '○'}</span>
                              <span>{resolveText(opt.nameKey)}</span>
                              {opt.additionalPrice > 0 && (
                                <span className="menu-card__option-price">+{sym}{opt.additionalPrice.toFixed(2)}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Notes */}
                  <div className="menu-card__notes">
                    <input
                      type="text"
                      placeholder="Add a note (e.g. no onions)..."
                      value={itemNotes[item.id] || ''}
                      onChange={(e) => updateCartItemNotes(item.id, e.target.value)}
                      maxLength={100}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Name picker for multi-guest */}
      {showNamePicker && menuData.guestCount > 1 && (
        <div className="menu-card__name-picker">
          <span className="menu-card__name-label">Who's ordering?</span>
          <div className="menu-card__name-options">
            {menuData.usedGuestNames.map(name => (
              <button
                key={name}
                className={`menu-card__name-btn ${orderName === name ? 'active' : ''}`}
                onClick={() => { setOrderName(name); setShowNamePicker(false); }}
              >
                {name}
              </button>
            ))}
            <input
              type="text"
              placeholder="Enter name..."
              value={orderName}
              onChange={(e) => setOrderName(e.target.value)}
              className="menu-card__name-input"
            />
          </div>
        </div>
      )}

      {/* Shared slot confirmation dialog */}
      {sharedSlotConfirm && (
        <div className="menu-card__shared-slot" style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 14px', margin: '0 8px 8px' }}>
          <div style={{ fontSize: 13, color: '#92400E', marginBottom: 8 }}>
            ⚠️ {sharedSlotConfirm.message}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ flex: 1, padding: '6px 12px', borderRadius: 6, border: '1px solid #D97706', background: '#FDE68A', color: '#92400E', fontSize: 13, cursor: 'pointer' }}
              onClick={() => placeOrder(true)}
              disabled={placing}
            >
              Confirm
            </button>
            <button
              style={{ flex: 1, padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' }}
              onClick={() => setSharedSlotConfirm(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Order confirmation step — shown for every order before placing */}
      {confirming && (
        <div className="menu-card__order-confirm" style={{ background: '#EFF6FF', border: '1px solid #3B82F6', borderRadius: 8, padding: '12px 14px', margin: '0 8px 8px' }}>
          <div style={{ fontSize: 13, color: '#1E40AF', marginBottom: 8, fontWeight: 600 }}>
            Confirm your order
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
            {cart.map(c => {
              const charged = (menuData?.isBeyondFreeLimit ?? false) && (c.menuItem.price ?? 0) > 0;
              return (
                <div key={c.menuItem.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#1E3A8A' }}>
                  <span>{c.quantity}× {resolveText(c.menuItem.nameKey)}</span>
                  <span>{charged ? `${sym}${((c.menuItem.price ?? 0) * c.quantity).toFixed(2)}` : 'Included'}</span>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 13, color: '#1E40AF', marginBottom: cartTotal > 0 && menuData?.isBeyondFreeLimit ? 6 : 10, fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
            <span>Total</span>
            <span>{cartTotal > 0 && menuData?.isBeyondFreeLimit ? `${sym}${cartTotal.toFixed(2)}` : 'Complimentary'}</span>
          </div>
          {cartTotal > 0 && menuData?.isBeyondFreeLimit && (
            <div style={{ fontSize: 12, color: '#1E40AF', marginBottom: 10 }}>
              💳 This order will be charged to your folio.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ flex: 1, padding: '6px 12px', borderRadius: 6, border: '1px solid #2563EB', background: '#3B82F6', color: '#fff', fontSize: 13, cursor: 'pointer' }}
              onClick={() => { setConfirming(false); placeOrder(); }}
              disabled={placing}
            >
              {placing ? 'Placing...' : 'Confirm & Place Order'}
            </button>
            <button
              style={{ flex: 1, padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' }}
              onClick={() => setConfirming(false)}
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Cart summary + place order */}
      {cart.length > 0 && (
        <div className="menu-card__cart">
          <div className="menu-card__cart-summary">
            <span className="menu-card__cart-count">{cartCount} item{cartCount > 1 ? 's' : ''}</span>
            {cartTotal > 0 && menuData?.isBeyondFreeLimit && <span className="menu-card__cart-total">{sym}{cartTotal.toFixed(2)}</span>}
          </div>
          <button
            className="menu-card__order-btn"
            onClick={() => (orderWillCharge && !isDeviceTrusted()) ? promptVerifyToOrder(() => setConfirming(true)) : setConfirming(true)}
            disabled={placing || confirming}
          >
            {placing ? (
              <span className="menu-card__spinner-sm" />
            ) : (orderWillCharge && !isDeviceTrusted()) ? (
              `🔒 ${t('verify.verifyToCharge') || 'Verify to charge to room'}`
            ) : (
              'Place Order'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
